import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../features/auth/AuthProvider.js";
import { ApiClientError } from "../services/api-client.js";
import * as inventoryApi from "../services/inventory-api.js";
import type { Warehouse } from "../types/api.js";

type FormState = { code: string; name: string; isActive: boolean };

const EMPTY_FORM: FormState = { code: "", name: "", isActive: true };

function isPlatformRole(role: string): boolean {
  return role === "superadmin" || role === "platform-support";
}

function canMutate(role: string): boolean {
  return isPlatformRole(role) || role === "tenant-admin";
}

function fromWarehouse(w: Warehouse): FormState {
  return { code: w.code, name: w.name, isActive: w.isActive };
}

export function WarehousesPage(): JSX.Element {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formTenantId, setFormTenantId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const allowMutation = !!user && canMutate(user.role);
  const isPlatform = !!user && isPlatformRole(user.role);

  const tenantQuery = useMemo(() => {
    if (user?.tenantId) return { tenantId: user.tenantId };
    return {};
  }, [user?.tenantId]);

  const friendlyError = useCallback(
    (e: unknown): string => {
      if (e instanceof ApiClientError) {
        if (e.status === 401) return t("errors.unauthorized");
        if (e.status === 403) return t("errors.forbidden");
        if (e.status === 404) return t("errors.notFound");
        if (e.status === 409) return e.message;
        if (e.status >= 400 && e.status < 500) return e.message || t("errors.badRequest");
        return e.message || t("errors.generic");
      }
      return t("errors.generic");
    },
    [t],
  );

  const load = useCallback(async () => {
    if (!token) return;
    setLoadState("loading");
    setErrorMsg(null);
    try {
      const { warehouses: rows } = await inventoryApi.fetchWarehouses(token, tenantQuery);
      setWarehouses(rows);
      setLoadState("ok");
    } catch (e) {
      setLoadState("error");
      setErrorMsg(friendlyError(e));
    }
  }, [token, tenantQuery, friendlyError]);

  useEffect(() => {
    void load();
  }, [load]);

  const startCreate = useCallback(() => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormTenantId("");
    setFormError(null);
    setShowCreate(true);
  }, []);

  const startEdit = useCallback((w: Warehouse) => {
    setShowCreate(false);
    setEditingId(w.id);
    setForm(fromWarehouse(w));
    setFormTenantId("");
    setFormError(null);
  }, []);

  const cancelForm = useCallback(() => {
    setShowCreate(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormTenantId("");
    setFormError(null);
  }, []);

  const submit = useCallback(async () => {
    if (!token) return;
    setFormError(null);
    setSubmitting(true);
    try {
      if (editingId) {
        const payload: inventoryApi.WarehouseUpdateInput = {
          code: form.code.trim(),
          name: form.name.trim(),
          isActive: form.isActive,
        };
        const { warehouse } = await inventoryApi.updateWarehouseRequest(token, editingId, payload);
        setWarehouses((prev) => prev.map((w) => (w.id === editingId ? warehouse : w)));
        cancelForm();
      } else {
        const payload: inventoryApi.WarehouseCreateInput = {
          code: form.code.trim(),
          name: form.name.trim(),
          isActive: form.isActive,
        };
        if (isPlatform && formTenantId.trim()) {
          payload.tenantId = formTenantId.trim();
        }
        const { warehouse } = await inventoryApi.createWarehouseRequest(token, payload);
        setWarehouses((prev) => [warehouse, ...prev]);
        cancelForm();
      }
    } catch (e) {
      setFormError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  }, [token, editingId, form, formTenantId, isPlatform, friendlyError, cancelForm]);

  const handleDelete = useCallback(
    async (w: Warehouse) => {
      if (!token) return;
      const message = t("common.confirmDelete", { name: w.code });
      if (!window.confirm(message)) return;
      try {
        await inventoryApi.deleteWarehouseRequest(token, w.id);
        setWarehouses((prev) => prev.map((row) => (row.id === w.id ? { ...row, isActive: false } : row)));
      } catch (e) {
        setErrorMsg(friendlyError(e));
      }
    },
    [token, t, friendlyError],
  );

  return (
    <div className="warehouses-page">
      <header className="page-header">
        <h1>{t("portal.warehouses.title")}</h1>
        <p className="text-muted">{t("portal.warehouses.subtitle")}</p>
      </header>

      {errorMsg ? <p className="form-error">{errorMsg}</p> : null}

      {allowMutation && !showCreate && editingId == null ? (
        <div className="actions-row">
          <button type="button" className="btn btn-primary" onClick={startCreate}>
            {t("portal.warehouses.new")}
          </button>
        </div>
      ) : null}

      {(showCreate || editingId != null) && allowMutation ? (
        <section className="card create-order-card">
          <h2 className="section-title">
            {editingId ? t("portal.warehouses.edit") : t("portal.warehouses.new")}
          </h2>
          {formError ? <p className="form-error">{formError}</p> : null}
          <div className="filters-row">
            <label className="field-inline field-grow">
              <span>{t("portal.warehouses.code")}</span>
              <input
                type="text"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                maxLength={32}
                required
              />
            </label>
            <label className="field-inline field-grow">
              <span>{t("portal.warehouses.name")}</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={200}
                required
              />
            </label>
            <label className="field-inline">
              <span>{t("common.status")}</span>
              <select
                value={form.isActive ? "active" : "inactive"}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.value === "active" }))}
              >
                <option value="active">{t("common.active")}</option>
                <option value="inactive">{t("common.inactive")}</option>
              </select>
            </label>
          </div>
          {isPlatform && !editingId ? (
            <label className="field field-block">
              <span>
                {t("portal.warehouses.tenantId")}{" "}
                <small className="text-muted">{t("portal.warehouses.tenantIdHint")}</small>
              </span>
              <input
                type="text"
                value={formTenantId}
                onChange={(e) => setFormTenantId(e.target.value)}
                placeholder="uuid"
              />
            </label>
          ) : null}
          <div className="status-actions">
            <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={submitting}>
              {submitting ? t("common.loading") : t("common.save")}
            </button>
            <button type="button" className="btn" onClick={cancelForm} disabled={submitting}>
              {t("common.cancel")}
            </button>
          </div>
        </section>
      ) : null}

      {loadState === "loading" ? <p className="text-muted">{t("common.loading")}</p> : null}

      {loadState === "ok" ? (
        <section className="card table-section">
          <h2 className="section-title">
            {t("portal.warehouses.list")} <span className="text-muted small">({warehouses.length})</span>
          </h2>
          {warehouses.length === 0 ? (
            <p className="text-muted empty-hint">{t("common.noResults")}</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t("portal.warehouses.code")}</th>
                    <th>{t("portal.warehouses.name")}</th>
                    <th>{t("common.status")}</th>
                    {allowMutation ? <th>{t("common.actions")}</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {warehouses.map((w) => (
                    <tr key={w.id}>
                      <td>
                        <code>{w.code}</code>
                      </td>
                      <td>{w.name}</td>
                      <td>
                        <span className={w.isActive ? "badge-active" : "badge-inactive"}>
                          {w.isActive ? t("common.active") : t("common.inactive")}
                        </span>
                      </td>
                      {allowMutation ? (
                        <td className="nowrap">
                          <button type="button" className="btn btn-sm" onClick={() => startEdit(w)}>
                            {t("common.edit")}
                          </button>{" "}
                          {w.isActive ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => void handleDelete(w)}
                            >
                              {t("common.delete")}
                            </button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
